import { useEffect, useMemo, useState } from "react";
import { Form } from 'react-bootstrap';
import CommonModal, {
  CommonModalFooter,
} from "../shared/modalLayout/CommonModal";
import Button from "../shared/buttons/button";
import AudiologistDeliveryFields from "./AudiologistDeliveryFields";
import { AddSvg } from "../../svgFiles/AddSvg";
import {
  useLazyGetVaBundleQuery,
  useGetVaDemoOrderOptionsQuery,
  useGetVaDemoFacilitiesQuery,
  useRequestVaDemoOrderMutation,
} from "../../app/vaApi/vaApi";
import { getAudiologistBundleDetailRows } from "../../helpers/vaInvoiceUtils";
import { US_STATES } from "../../mocks/va/mockData";
import { showToast } from "../../helpers/Utils";
import "../../styles/requestDemoUnitModal.css";

function DemoSuccessIcon() {
  return (
    <svg
      width="56"
      height="56"
      viewBox="0 0 56 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="28" cy="28" r="28" fill="#155EEF" />
      <path
        d="M24.5 36.5L16.75 28.75L19.1 26.4L24.5 31.8L36.9 19.4L39.25 21.75L24.5 36.5Z"
        fill="white"
      />
    </svg>
  );
}

const PRODUCT_CATEGORIES = [
  { code: 'all', label: 'All' },
  { code: 'va_bundles', label: 'VA Bundles' },
  { code: 'replacements', label: 'Replacements' },
  { code: 'marketing_materials', label: 'Marketing materials' },
  { code: 'conference_materials', label: 'Conference materials' },
];

const FALLBACK_OPTIONS = [
  { code: "CAP1", label: "CAP-1" },
  { code: "CAP2", label: "CAP-2" },
];


const EMPTY_DELIVERY = {
  firstName: "",
  lastName: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  email: "",
};

function BundleDetailValue({ items }) {
  if (!items?.length) return <span>—</span>;
  if (items.length === 1) return <span>{items[0]}</span>;
  return <span>{items.join(", ")}</span>;
}

function displayBundleCode(code) {
  const normalized = normalizeBundleCode(code);
  if (normalized === "CAP1") return "CAP-1";
  if (normalized === "CAP2") return "CAP-2";
  return code || "";
}

function resolveStateCode(stateValue = "") {
  const raw = String(stateValue || "").trim();
  if (!raw) return "";
  const byCode = US_STATES.find(
    (s) => s.code.toLowerCase() === raw.toLowerCase()
  );
  if (byCode) return byCode.code;
  const byName = US_STATES.find(
    (s) => s.name.toLowerCase() === raw.toLowerCase()
  );
  return byName?.code || raw;
}

function isDeliveryComplete(delivery) {
  return [
    delivery.firstName,
    delivery.lastName,
    delivery.address1,
    delivery.city,
    delivery.state,
    delivery.zip,
    delivery.phone,
    delivery.email,
  ].every((value) => String(value || "").trim());
}

function normalizeBundleCode(code) {
  return String(code || "")
    .trim()
    .toUpperCase()
    .replace(/[-\s]/g, "");
}

export default function RequestDemoUnitModal({ show, onClose }) {
  const { data: apiOptions, isError: optionsError, refetch: reloadOptions } = useGetVaDemoOrderOptionsQuery(undefined, {
    skip: !show,
  });

  const options = useMemo(() => {
    const fromApi =
      Array.isArray(apiOptions) && apiOptions.length > 0
        ? apiOptions
        : FALLBACK_OPTIONS;
    return fromApi.map((option) => ({
      code: normalizeBundleCode(option.code),
      label: option.label || option.code,
      kind: option.kind || 'bundle',
      category: option.category || 'va_bundles',
      available: option.available !== false,
    }));
  }, [apiOptions]);

  const [loadBundle] = useLazyGetVaBundleQuery();
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [requestDemoOrder, { isLoading: isSubmitting }] = useRequestVaDemoOrderMutation();
  const [cart, setCart] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftCodes, setDraftCodes] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState('all');
  const [delivery, setDelivery] = useState(EMPTY_DELIVERY);
  const [facilityId, setFacilityId] = useState('');
  const [audiologistName, setAudiologistName] = useState('');
  const [notes, setNotes] = useState('');
  const { data: facilities = [], isLoading: facilitiesLoading, isError: facilitiesError, refetch: reloadFacilities } = useGetVaDemoFacilitiesQuery(undefined, { skip: !show });
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (!show) return;
    let active = true;
    setCatalogLoading(true);
    setCatalogError(false);
    // The selectable catalog follows server configuration rather than a fixed CAP list.
    Promise.allSettled(options.map(async (option) => {
      if (option.kind === 'item') return option;
      const response = await loadBundle(option.code, retry === 0).unwrap();
      if (!getAudiologistBundleDetailRows(response?.data || {}).length) throw new Error("Product details unavailable");
      return { ...response.data, ...option };
    })).then((results) => {
      if (!active) return;
      setCatalog(results.filter((result) => result.status === "fulfilled").map((result) => result.value));
      setCatalogError(results.some((result) => result.status === "rejected"));
      setCatalogLoading(false);
    });
    return () => { active = false; };
  }, [show, options, loadBundle, retry]);

  const cartByCode = useMemo(() => {
    const map = new Map();
    cart.forEach((row) => map.set(row.code, row));
    return map;
  }, [cart]);

  useEffect(() => {
    if (!show) {
      setCart([]);
      setPickerOpen(false);
      setDraftCodes([]);
      setSearch("");
      setDelivery(EMPTY_DELIVERY);
      setFacilityId('');
      setAudiologistName('');
      setNotes('');
      setSubmitSuccess(false);
    }
  }, [show]);

  const canConfirm =
    cart.length > 0 && cart.every((row) => catalog.some((product) => product.code === row.code)) &&
    cart.every(row => catalog.find(product => product.code === row.code)?.available !== false) &&
    !catalogLoading && isDeliveryComplete(delivery) && !isSubmitting;

  const handleDeliveryChange = (event) => {
    const { name, value } = event.target;
    setDelivery((prev) => ({ ...prev, [name]: value }));
  };

  const handlePlaceSelect = (place) => {
    setDelivery((prev) => ({
      ...prev,
      address1: place.address1 ?? prev.address1,
      city: place.city ?? prev.city,
      state: resolveStateCode(place.state) || prev.state,
      zip: place.zip ?? prev.zip,
    }));
  };

  const handleToggleProduct = (code, checked) => {
    setDraftCodes((prev) => checked ? [...new Set([...prev, code])] : prev.filter((entry) => entry !== code));
  };

  const handleQuantityChange = (code, nextQty) => {
    const normalized = normalizeBundleCode(code);
    const quantity = Math.max(1, Math.min(4, Number(nextQty) || 1));
    setCart((prev) =>
      prev.map((row) =>
        row.code === normalized ? { ...row, quantity } : row
      )
    );
  };

  const handleOpenBundles = () => {
    setDraftCodes(cart.map((row) => row.code));
    setSearch("");
    setCategory('all');
    setPickerOpen(true);
  };

  const handleSubmit = async () => {
    if (!cart.length) {
      showToast("Select at least one product to continue.", "error");
      return;
    }
    if (!isDeliveryComplete(delivery)) {
      showToast("Fill in all delivery details to continue.", "error");
      return;
    }

    try {
      // API validates + returns 202 quickly; Shopify create runs in background.
      await requestDemoOrder({
        facilityId: facilityId || undefined,
        audiologistName: audiologistName.trim(),
        notes: notes.trim(),
        items: cart.map((row) => ({
          optionCode: row.code,
          quantity: row.quantity,
          reason: (row.reason || '').trim(),
        })),
        delivery: {
          firstName: String(delivery.firstName || "").trim(),
          lastName: String(delivery.lastName || "").trim(),
          address1: String(delivery.address1 || "").trim(),
          address2: String(delivery.address2 || "").trim(),
          city: String(delivery.city || "").trim(),
          state: String(delivery.state || "").trim(),
          zip: String(delivery.zip || "").trim(),
          phone: String(delivery.phone || "").trim(),
          email: String(delivery.email || "").trim(),
          country: "US",
        },
      }).unwrap();
      setSubmitSuccess(true);
    } catch (error) {
      const message =
        error?.data?.message ||
        error?.error ||
        error?.message ||
        "Could not create demo unit request.";
      showToast(message, "error");
    }
  };

  const handleCloseSuccess = () => {
    setSubmitSuccess(false);
    onClose?.();
  };

  const catalogByCode = new Map(catalog.map((product) => [product.code, product]));
  const visibleProducts = catalog.filter((product) => {
    if (category !== 'all' && product.category !== category) return false;
    const text = [product.label, displayBundleCode(product.code),
      ...getAudiologistBundleDetailRows(product).flatMap((row) => [row.label, ...row.items])].join(" ").toLowerCase();
    return text.includes(search.trim().toLowerCase());
  });
  const applyProducts = () => {
    setCart(draftCodes.map((code) => cartByCode.get(code) || { code, quantity: 1, reason: '' }));
    setPickerOpen(false);
  };
  const detailOrder = ["Glasses", "Frame", "Sunglasses", "Accessories", "Services", "Device", "Handheld"];
  const detailRank = (label) => detailOrder.includes(label) ? detailOrder.indexOf(label) : detailOrder.length;
  const renderDetails = (product, quantity) => (
    <ul className="request-demo-unit-modal__details">
      {getAudiologistBundleDetailRows(product).sort((a, b) => detailRank(a.label) - detailRank(b.label)).map((row) => (
        <li key={row.label}>
          <span>{row.label === "Frame" ? "Glasses" : row.label}</span>
          <span><BundleDetailValue items={row.items} />{quantity != null && <span className="request-demo-unit-modal__quantity-badge">{quantity}</span>}</span>
        </li>
      ))}
    </ul>
  );

  if (submitSuccess) {
    return (
      <CommonModal
        show={show}
        title=""
        centered
        className="request-demo-unit-modal request-demo-unit-modal--success modal-sm"
        onHide={handleCloseSuccess}
        closeOnBackdrop
      >
        <div className="request-demo-unit-modal__success">
          <DemoSuccessIcon />
          <p className="request-demo-unit-modal__success-message">
            Your demo request has been submitted successfully.
          </p>
          <p className="request-demo-unit-modal__success-note">
            The order is being created in the background and may take a little
            while to appear in your list.
          </p>
        </div>
      </CommonModal>
    );
  }

  if (pickerOpen) {
    return (
      <CommonModal
        show={show}
        title="Select Product"
        centered
        className="request-demo-unit-modal request-demo-product-picker modal-md"
        onHide={() => setPickerOpen(false)}
        closeOnBackdrop
        footer={<div className="request-demo-product-picker__footer">
          <span role="status">{draftCodes.length} {draftCodes.length === 1 ? "product" : "products"} selected</span>
          <div className="d-flex gap-2">
            <Button label="Cancel" className="cancel" size="lg" onClick={() => setPickerOpen(false)} />
            <Button label="Add" size="lg" onClick={applyProducts} disabled={catalogLoading || draftCodes.some((code) => !catalogByCode.has(code) || catalogByCode.get(code)?.available === false)} />
          </div>
        </div>}
      >
        <input type="search" className="request-demo-product-picker__search" aria-label="Search products" placeholder="Select Product" value={search} onChange={(event) => setSearch(event.target.value)} />
        <div className="request-demo-product-picker__categories" role="tablist" aria-label="Product categories">
          {PRODUCT_CATEGORIES.map(item => <button type="button" key={item.code} role="tab" aria-selected={category === item.code} className={category === item.code ? 'is-active' : ''} onClick={() => setCategory(item.code)}>{item.label}</button>)}
        </div>
        <div className="request-demo-product-picker__heading">Product</div>
        <div className="request-demo-product-picker__list" aria-busy={catalogLoading}>
          {catalogLoading && <p className="request-demo-unit-modal__hint" role="status">Loading products…</p>}
          {(catalogError || optionsError) && <div role="alert" className="request-demo-product-picker__error">Some products could not be loaded. <button type="button" className="request-demo-unit-modal__link-btn" onClick={() => { reloadOptions(); setRetry((value) => value + 1); }}>Retry</button></div>}
          {!catalogLoading && visibleProducts.length === 0 && <p className="request-demo-unit-modal__hint">{search.trim() ? "No matching products." : "No products available."}</p>}
          {visibleProducts.map((product) => (
            <div className="request-demo-product-picker__item" key={product.code}>
              <label className="request-demo-unit-modal__cart-select">
                <input type="checkbox" className="request-demo-unit-modal__checkbox" checked={draftCodes.includes(product.code)} disabled={product.available === false} onChange={(event) => handleToggleProduct(product.code, event.target.checked)} />
                <span className="request-demo-unit-modal__cart-title">{displayBundleCode(product.label || product.code)}</span>
              </label>
              {product.available === false && <span className="request-demo-unit-modal__hint">Currently unavailable</span>}
              {product.kind === 'bundle' && renderDetails(product)}
            </div>
          ))}
        </div>
      </CommonModal>
    );
  }

  return (
    <CommonModal
      show={show}
      title="Request demo"
      centered
      className="request-demo-unit-modal request-demo-order-modal modal-md"
      onHide={() => { if (!isSubmitting) onClose?.(); }}
      closeOnBackdrop={false}
      footer={<CommonModalFooter className="request-demo-unit-modal__footer common-modal-footer d-flex gap-3 mt-4" align="start">
        <Button label="Confirm" size="lg" onClick={handleSubmit} loading={isSubmitting} disabled={!canConfirm} />
        <Button label="Cancel" className="cancel" size="lg" onClick={onClose} disabled={isSubmitting} />
      </CommonModalFooter>}
    >
      <div className="request-demo-unit-modal__content">
        <section className="request-demo-unit-modal__section">
          <div className="request-demo-unit-modal__section-head">
            <h6 className="request-demo-unit-modal__section-title">Product</h6>
            <button type="button" className="request-demo-unit-modal__add-product" onClick={handleOpenBundles} disabled={isSubmitting}><AddSvg /><span>Add Product</span></button>
          </div>
          {cart.length === 0 ? <div className="request-demo-unit-modal__product-empty"><span className="request-demo-unit-modal__hint">Add products to your demo request.</span></div> : (
            <div className="request-demo-unit-modal__selected-products">
              {cart.map((row) => {
                const product = catalogByCode.get(row.code);
                return <article className="request-demo-unit-modal__cart-card" key={row.code}>
                  <div className="request-demo-unit-modal__cart-top">
                    <h6 className="request-demo-unit-modal__cart-title">{displayBundleCode(product?.label || row.code)}</h6>
                    <div className="request-demo-unit-modal__cart-controls">
                      <div className="request-demo-unit-modal__qty" role="group" aria-label={`${displayBundleCode(row.code)} quantity`}>
                        <button type="button" className="request-demo-unit-modal__qty-btn" aria-label={`Decrease ${displayBundleCode(row.code)} quantity`} disabled={isSubmitting || row.quantity <= 1} onClick={() => handleQuantityChange(row.code, row.quantity - 1)}>−</button>
                        <span className="request-demo-unit-modal__qty-value" aria-live="polite">{row.quantity}</span>
                        <button type="button" className="request-demo-unit-modal__qty-btn" aria-label={`Increase ${displayBundleCode(row.code)} quantity`} disabled={isSubmitting || row.quantity >= 4} onClick={() => handleQuantityChange(row.code, row.quantity + 1)}>+</button>
                      </div>
                      <button type="button" className="request-demo-unit-modal__remove" aria-label={`Remove ${displayBundleCode(row.code)}`} disabled={isSubmitting} onClick={() => setCart((prev) => prev.filter((item) => item.code !== row.code))}>×</button>
                    </div>
                  </div>
                  {product ? (product.kind === 'bundle' ? renderDetails(product, row.quantity) : null) : <span className="request-demo-unit-modal__hint">Product details unavailable. Please add this product again.</span>}
                  <Form.Group controlId={`demo-reason-${row.code}`} className="request-demo-unit-modal__reason">
                    <Form.Label className="visually-hidden">Request reason for {product?.label || row.code} (Optional)</Form.Label>
                    <Form.Control as="textarea" rows={2} className="cmn_input" placeholder="Why are you requesting this? (Optional)" maxLength={1000} value={row.reason || ''} disabled={isSubmitting} onChange={event => setCart(prev => prev.map(item => item.code === row.code ? {...item, reason: event.target.value} : item))} />
                  </Form.Group>
                </article>;
              })}
            </div>
          )}
        </section>
        <section className="request-demo-unit-modal__optional-section">
          <Form.Group controlId="demo-facility">
            <Form.Label>Facility it's for (Optional)</Form.Label>
            <Form.Select className="cmn_input" value={facilityId} onChange={event => setFacilityId(event.target.value)} disabled={isSubmitting || facilitiesLoading}>
              <option value="">{facilitiesLoading ? 'Loading facilities…' : 'Select facility (optional)'}</option>
              {facilities.map(facility => <option key={facility.id} value={facility.id}>{[facility.name, facility.city, facility.stateCode].filter(Boolean).join(' · ')}</option>)}
            </Form.Select>
            {facilitiesError && <p role="alert" className="request-demo-unit-modal__hint">Facilities could not be loaded. You can continue without one, or <button type="button" className="request-demo-unit-modal__link-btn" onClick={reloadFacilities}>retry</button>.</p>}
          </Form.Group>
          <Form.Group controlId="demo-audiologist">
            <Form.Label>Audiologist it's for (Optional)</Form.Label>
            <Form.Control className="cmn_input" value={audiologistName} onChange={event => setAudiologistName(event.target.value)} placeholder="Enter audiologist name" maxLength={250} disabled={isSubmitting} />
          </Form.Group>
        </section>
        <AudiologistDeliveryFields title="Ship to" delivery={delivery} onChange={handleDeliveryChange} onPlaceSelect={handlePlaceSelect} emailPlaceholder="Enter your email" />
        <section className="request-demo-unit-modal__optional-section">
          <Form.Group controlId="demo-notes">
            <Form.Label>Notes (Optional)</Form.Label>
            <Form.Control as="textarea" rows={3} className="cmn_input" value={notes} onChange={event => setNotes(event.target.value)} maxLength={5000} disabled={isSubmitting} />
          </Form.Group>
        </section>
      </div>
    </CommonModal>
  );
}
